import { usePermissionsRepository } from '@Hooks/useRepository';
import { type EnumPartnershipType } from '@Model';
import { PartnershipsRepository } from '@Repositories/Permissions';
import { isNullOrEmpty, isOk } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export function usePartnershipInfo(addressId: string, partnershipType: EnumPartnershipType, enabled: boolean) {
    const partnerships = usePermissionsRepository(PartnershipsRepository);

    const {
        data: queryResult,
        isLoading,
        refetch,
    } = useQuery({
        queryFn: () => partnerships.getPartnershipInfo(addressId, partnershipType),
        queryKey: ['partnerships', 'info', addressId, partnershipType],
        enabled: enabled && !isNullOrEmpty(addressId),
    });

    const info = isOk(queryResult) ? queryResult.data : undefined;

    const existingUserEmails = useMemo(
        () => new Set((info?.existing_user_emails ?? []).map((email) => email.toLowerCase().trim())),
        [info],
    );

    const pendingUserEmails = useMemo(
        () => new Set((info?.pending_user_emails ?? []).map((email) => email.toLowerCase().trim())),
        [info],
    );

    const rejectedUserEmails = useMemo(
        () => new Set((info?.rejected_user_emails ?? []).map((email) => email.toLowerCase().trim())),
        [info],
    );

    return useMemo(
        () => ({
            partnership: info?.partnership ?? undefined,
            existingUserEmails,
            pendingUserEmails,
            rejectedUserEmails,
            isLoading,
            refetch: () => {
                void refetch();
            },
        }),
        [info, existingUserEmails, pendingUserEmails, rejectedUserEmails, isLoading, refetch],
    );
}
